import React, { useState } from 'react';
import styled from "styled-components";
import { getTheme } from "../../shared/themeUtils";
import { useRecentlyViewedProducts } from "../../hooks/useRecentlyViewedProducts";
import ProductCarousel from "./ProductCarousel";
import Icon from "../Icon";
import Button from "../Button";
import { IProductSpecial } from "../../interfaces/IProductSpecial";
import { IClickSource } from "../../interfaces/IClickSource";
import { useMessages } from "../../hooks/useMessages";

// Section separator for content sections
const SectionSeparator = styled.div`
    height: 1px;
    background-color: ${getTheme("colors.border")};
    width: 100%;
    margin: ${getTheme("space.4")} 0 ${getTheme("space.3")} 0;
`

// Styles for the remove button on product carousel items
const ProductRemoveButton = styled.button`
    position: absolute;
    top: ${getTheme("space.1")};
    left: ${getTheme("space.1")};
    z-index: 10;
    background-color: rgba(255, 255, 255, 0.8);
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border: 1px solid ${getTheme("colors.border")};
    transition: all 0.2s ease;
    padding: 0;

    &:hover {
        background-color: ${getTheme("colors.background")};
        transform: scale(1.05);
    }

    .icon {
        opacity: 0.8;
    }
`

interface RecentlyViewedProductsProps {
  clickSource?: IClickSource;
  showSeparator?: boolean;
  className?: string;
}

const RecentlyViewedProducts: React.FC<RecentlyViewedProductsProps> = ({ 
  clickSource, 
  showSeparator = true,
  className = ''
}) => {
  const [isRemovingProduct, setIsRemovingProduct] = useState(false);
  const messagesQuery = useMessages();
  const { recentProducts, removeProduct, clearAllProducts } = useRecentlyViewedProducts();

  // Add the remove button to each product in the recently viewed list
  const recentProductsWithButtons = recentProducts?.map((product: IProductSpecial) => ({
    ...product,
    // Add a custom overlay element to each product
    customOverlay: (
      <ProductRemoveButton
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsRemovingProduct(true);
          removeProduct(product.productID);
          // Reset the state after a short delay
          setTimeout(() => {
            setIsRemovingProduct(false);
          }, 300);
        }}
        title="Remove from recently viewed"
      >
        <Icon variant="small">close-line</Icon>
      </ProductRemoveButton>
    )
  }));
  
  // If no products and not in the middle of removing, don't render anything
  if (recentProducts.length === 0 && !isRemovingProduct) {
    return null;
  }
  
  // If messages aren't loaded yet, don't render
  if (!messagesQuery.data) {
    return null;
  }

  return (
    <div className={className}>
      {showSeparator && <SectionSeparator />}
      <div className="mt-3">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <h2>{messagesQuery.data.messages["recently-viewed"]}</h2>
          {/* Only show Clear All button when there are 2 or more products */}
          {recentProducts.length > 1 && (
            <Button
              variant="secondary"
              onClick={() => {
                setIsRemovingProduct(true);
                clearAllProducts();
                // Reset after a short delay
                setTimeout(() => {
                  setIsRemovingProduct(false);
                }, 300);
              }}
            >
              <Icon variant="small">close-line</Icon>
            </Button>
          )}
        </div>
        <ProductCarousel items={recentProductsWithButtons || []} clickSource={clickSource} />
      </div>
    </div>
  );
};

export default RecentlyViewedProducts; 